import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Heart, ShieldCheck, Users, Loader2 } from "lucide-react";
import { Waivers } from "@/components/Waivers";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";

// Lightweight registration form for day-events, workshops, and sister
// circles. Runs BEFORE Stripe checkout so Holly has each attendee's phone,
// waivers, and emergency contact on file — critical for cancellation
// outreach when a class has to be rescheduled or cancelled.
export default function RegisterEvent() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offering, setOffering] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    physical_limitations: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    how_heard: "",
    liability_waiver_agreed: false,
    photo_waiver_agreed: false,
    subscribe_to_newsletter: true,
  });
  const [busy, setBusy] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Fetch offering + prefilled profile in parallel.
  useEffect(() => {
    if (user === false) {
      navigate("/login", { state: { from: `/register-event/${slug}` } });
      return;
    }
    if (!user) return;
    (async () => {
      try {
        const [o, p] = await Promise.all([
          api.get(`/offerings/${slug}`),
          api.get("/events/my-profile"),
        ]);
        setOffering(o.data);
        setForm((f) => ({ ...f, ...(p.data || {}), liability_waiver_agreed: false, photo_waiver_agreed: false }));
      } catch {
        toast.error("Could not load event details.");
      }
    })();
  }, [slug, user, navigate]);

  const submit = async () => {
    const required = ["full_name", "email", "phone", "emergency_contact_name", "emergency_contact_phone"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please fill in every required field.");
        document.querySelector(`[data-field="${k}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    if (!form.liability_waiver_agreed || !form.photo_waiver_agreed) {
      toast.error("Please read and agree to both the liability waiver and photo release.");
      document.querySelector('[data-field="liability_waiver_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!form.physical_limitations.trim()) {
      toast.error('Please note any physical limitations — if none, write "none" or "n/a".');
      document.querySelector('[data-field="physical_limitations"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/events/register/${slug}`, {
        ...form,
        origin_url: window.location.origin,
      });
      if (data?.registration_id) {
        navigate(`/payment-event/${data.registration_id}`);
        return;
      }
      toast.error("Registration saved but couldn't route to payment. Please contact Holly.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit registration.");
    } finally {
      setBusy(false);
    }
  };

  if (!offering) {
    return (
      <div className="pt-40 min-h-screen container-aviana text-aviana-muted">
        <Loader2 size={22} className="inline animate-spin mr-2" /> Loading event…
      </div>
    );
  }

  const base = Number(offering.price_usd) || 0;
  const cardFee = Math.round((base * 0.029 + 0.30) * 100) / 100;
  const totalWithFee = Math.round((base + cardFee) * 100) / 100;

  return (
    <div className="pt-32 pb-24 min-h-screen bg-aviana-paper/40" data-testid="register-event-page">
      <SEO title={`Register · ${offering.title}`} path={`/register-event/${slug}`} description={`Reserve your spot at ${offering.title}.`} />
      <div className="container-aviana max-w-3xl">
        <Link to={`/offering/${slug}`} className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Back to event
        </Link>
        <p className="overline-plum mb-3">Reserve your spot</p>
        <h1 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">{offering.title}</h1>
        {offering.schedule_summary && (
          <p className="mt-4 text-aviana-inkSoft font-serif italic text-[17px]">{offering.schedule_summary}</p>
        )}
        <p className="mt-3 text-aviana-muted text-[14px]">
          A few quick details, then we'll take you to secure payment.{" "}
          <span className="text-aviana-plum">Your info is saved to your account so future events are one click.</span>
        </p>

        <div className="mt-10 space-y-10">
          <Section icon={Users} title="Contact information" subtitle="So Holly can reach you if anything changes.">
            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Full name *" name="full_name" value={form.full_name} onChange={(v) => update({ full_name: v })} />
              <FormField label="Email *" name="email" type="email" value={form.email} onChange={(v) => update({ email: v })} />
            </div>
            <FormField label="Phone (used only if we need to cancel or reschedule) *" name="phone" value={form.phone} onChange={(v) => update({ phone: v })} />
          </Section>

          <Section icon={Heart} title="Health & emergency contact" subtitle="All required — if none apply, please write &ldquo;none&rdquo; or &ldquo;n/a&rdquo;.">
            <FormField
              label="Physical limitations / injuries / concerns *"
              name="physical_limitations"
              value={form.physical_limitations}
              onChange={(v) => update({ physical_limitations: v })}
              placeholder='If none, write "none" or "n/a"'
              multiline
              rows={3}
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Emergency contact name *" name="emergency_contact_name" value={form.emergency_contact_name} onChange={(v) => update({ emergency_contact_name: v })} />
              <FormField label="Emergency contact phone *" name="emergency_contact_phone" value={form.emergency_contact_phone} onChange={(v) => update({ emergency_contact_phone: v })} />
            </div>
          </Section>

          <Section icon={Sparkles} title="How did you hear about us?" subtitle="Optional — helps Holly know what's working.">
            <FormField label="Where did you find this event?" name="how_heard" value={form.how_heard} onChange={(v) => update({ how_heard: v })} placeholder="Instagram · a friend · Google · newsletter…" />
          </Section>

          <Section icon={ShieldCheck} title="Waivers & consents" subtitle="Required for every Aviana registration — including reiki, yoga, aerial yoga, and workshop programming.">
            <Waivers
              liabilityAgreed={form.liability_waiver_agreed}
              onLiabilityChange={(v) => update({ liability_waiver_agreed: v })}
              photoAgreed={form.photo_waiver_agreed}
              onPhotoChange={(v) => update({ photo_waiver_agreed: v })}
              testidPrefix="event-waiver"
            />
            <label className="flex items-start gap-3 p-5 rounded-xl bg-aviana-gold/8 border border-aviana-gold/30 cursor-pointer">
              <input
                type="checkbox"
                checked={form.subscribe_to_newsletter}
                onChange={(e) => update({ subscribe_to_newsletter: e.target.checked })}
                className="w-5 h-5 mt-0.5 accent-aviana-gold"
                data-testid="newsletter-optin"
              />
              <span className="text-[14px] text-aviana-inkSoft leading-[1.6]">
                <strong className="text-aviana-ink">Yes, add me to Aviana's newsletter.</strong> A monthly note from Holly with upcoming retreats, new events, and a little seasonal magic. Unsubscribe anytime.
              </span>
            </label>
          </Section>

          <div className="pt-4">
            <div className="rounded-2xl bg-aviana-cream border border-aviana-border p-5 mb-4" data-testid="event-total-card">
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <p className="overline-plum">Event tuition</p>
                <p className="font-display text-3xl text-aviana-ink leading-none">${(Number(offering.price_usd) || 0).toFixed(2)}</p>
              </div>
              <div className="text-[12px] text-aviana-muted space-y-0.5 border-t border-aviana-border/60 pt-2">
                <p>Next you'll choose how to pay:</p>
                <p>· <strong className="text-aviana-ink">Card via Stripe</strong> — ${totalWithFee.toFixed(2)} total (+${cardFee.toFixed(2)} processing fee)</p>
                <p>· <strong className="text-aviana-ink">Venmo</strong> to @aviana_yoga — ${(Number(offering.price_usd) || 0).toFixed(2)} (no fees)</p>
                <p>· <strong className="text-aviana-ink">Zelle</strong> to avianayoga@gmail.com — ${(Number(offering.price_usd) || 0).toFixed(2)} (no fees)</p>
              </div>
            </div>
            <button
              onClick={submit}
              disabled={busy}
              className="btn-gold w-full !py-4 !text-[12px]"
              data-testid="submit-event-registration"
            >
              {busy ? "Saving registration…" : "Reserve my spot · choose payment →"}
            </button>
            <p className="text-[11px] text-aviana-muted text-center mt-3">Your details are saved right away. You'll pick Stripe, Venmo, or Zelle on the next screen.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="rounded-3xl bg-white border border-aviana-border p-6 sm:p-8">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
          <Icon size={16} />
        </div>
        <div>
          <h2 className="font-display text-2xl text-aviana-ink leading-tight">{title}</h2>
          {subtitle && <p className="text-[12px] text-aviana-muted mt-1" dangerouslySetInnerHTML={{ __html: subtitle }} />}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function FormField({ label, name, value, onChange, type = "text", multiline = false, rows = 3, placeholder = "" }) {
  return (
    <label className="block" data-field={name}>
      <span className="overline-plum mb-2 block">{label}</span>
      {multiline ? (
        <textarea
          rows={rows}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink leading-[1.55] focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30 placeholder:text-aviana-muted/60"
          data-testid={`event-${name}`}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30 placeholder:text-aviana-muted/60"
          data-testid={`event-${name}`}
        />
      )}
    </label>
  );
}
